import { supabase } from '../lib/supabase'
import type { WriteResult } from './projectWrites'
import { readSections, type CallSheetSections } from './callSheetSections'
import type {
  ShootDayContact,
  ShootDayKitItem,
  ShootDayScheduleItem,
  ShootDayShotItem,
} from './shootDaySource'

/**
 * Call sheets.
 *
 * Restructured 13 Sep 2026. A call sheet used to be a property of a project,
 * one apiece, enforced in the database. Tom hit the wall that creates: ATM is
 * one production with a build day, a tech test and four live days, and a
 * second call sheet meant inventing a second project (and the deliverables
 * that come with one) for a live TV show that has no deliverables.
 *
 * A call sheet is now its own thing:
 *   • it belongs to a CLIENT — the least you need to name one
 *   • it may point at a show and/or a project, which is where it inherits
 *     crew, client contact and location from
 *   • it has its OWN title and date
 *
 * Everything here goes through SECURITY DEFINER functions, never a direct
 * table read — same posture as the rest of this feature. Only the
 * publishable key is ever used.
 */

function fail(fn: string, error: { message: string; code?: string } | null): WriteResult {
  if (!error) return { ok: true }
  const detail = `${fn}: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] rpc refused — ${detail}`)
  return { ok: false, error: detail }
}

/** A row in the Call Sheets tab. */
export interface CallSheetSummary {
  id: string
  clientName: string
  title: string | null
  shootDate: string | null
  showId: string | null
  showName: string | null
  projectId: string | null
  projectName: string | null
  scheduleCount: number
  crewToken: string
  clientToken: string
  updatedAt: string
}

/** A person on this call sheet who holds a Portal OS account. */
export interface CallSheetCrewMember {
  userId: string
  name: string
  role: string | null
  phone: string | null
  /** True when they're here because the attached project's team says so,
      rather than having been picked for this particular day. Those rows are
      edited on the project's Team, not here. */
  fromProject: boolean
}

export interface StaffOption {
  userId: string
  name: string
  phone: string | null
}

/** Everything the builder needs for one call sheet. */
export interface CallSheetPayload {
  id: string
  clientName: string
  title: string | null
  shootDate: string | null
  showId: string | null
  showName: string | null
  projectId: string | null
  projectName: string | null
  projectSlug: string | null
  crewToken: string
  clientToken: string
  weather: string | null
  parkingNotes: string | null
  locationAddress: string | null
  locationMapUrl: string | null
  importantInstructions: string | null
  /** Legacy column from before the 8 Sep 2026 Important Instructions merge —
      still returned so old content can be folded in on first load. */
  clientNotes: string | null
  scriptUrl: string | null
  guestBiosUrl: string | null
  storyboardUrl: string | null
  dropboxUrl: string | null
  currentScheduleIndex: number
  sections: CallSheetSections
  clientContactName: string | null
  clientContactPhone: string | null
  crew: CallSheetCrewMember[]
  extraCrew: ShootDayContact[]
  clientContacts: ShootDayContact[]
  guests: ShootDayContact[]
  schedule: ShootDayScheduleItem[]
  shots: ShootDayShotItem[]
  kit: ShootDayKitItem[]
}

/** What a new call sheet can be attached to. */
export interface CallSheetTargets {
  clients: string[]
  shows: { id: string; name: string; clientName: string }[]
  projects: { id: string; name: string; clientName: string; showId: string | null; slug: string }[]
}

export interface NewCallSheet {
  clientName: string
  title: string
  shootDate: string
  showId: string | null
  projectId: string | null
}

export async function fetchCallSheets(): Promise<CallSheetSummary[]> {
  if (!supabase) return []
  const { data, error } = await supabase.rpc('list_call_sheets')
  if (error) {
    console.warn(`[supabase] list_call_sheets failed: ${error.message}`)
    return []
  }
  return (data as CallSheetSummary[] | null) ?? []
}

export async function fetchCallSheetTargets(): Promise<CallSheetTargets> {
  const empty: CallSheetTargets = { clients: [], shows: [], projects: [] }
  if (!supabase) return empty
  const { data, error } = await supabase.rpc('list_call_sheet_targets')
  if (error) {
    console.warn(`[supabase] list_call_sheet_targets failed: ${error.message}`)
    return empty
  }
  return (data as CallSheetTargets | null) ?? empty
}

export async function fetchCallSheet(id: string): Promise<CallSheetPayload | null> {
  if (!supabase || !id) return null
  const { data, error } = await supabase.rpc('get_call_sheet', { p_call_sheet_id: id })
  if (error) {
    console.warn(`[supabase] get_call_sheet failed: ${error.message}`)
    return null
  }
  if (!data) return null
  const payload = data as CallSheetPayload
  // readSections tolerates a payload from before the segment switches shipped.
  return { ...payload, sections: readSections(payload.sections) }
}

export async function createCallSheet(
  input: NewCallSheet,
): Promise<{ result: WriteResult; callSheet?: CallSheetPayload }> {
  if (!supabase) return { result: { ok: false, error: 'Supabase is not configured.' } }

  const { data, error } = await supabase.rpc('create_call_sheet', {
    p_client_name: input.clientName,
    p_title: input.title || null,
    p_shoot_date: input.shootDate || null,
    p_show_id: input.showId,
    p_project_id: input.projectId,
  })
  const result = fail('create_call_sheet', error)
  if (!result.ok || !data) return { result }

  const payload = data as CallSheetPayload
  return { result, callSheet: { ...payload, sections: readSections(payload.sections) } }
}

export async function updateCallSheetMeta(id: string, input: NewCallSheet): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase.rpc('update_call_sheet_meta', {
    p_call_sheet_id: id,
    p_client_name: input.clientName,
    p_title: input.title || null,
    p_shoot_date: input.shootDate || null,
    p_show_id: input.showId,
    p_project_id: input.projectId,
  })
  return fail('update_call_sheet_meta', error)
}

export async function deleteCallSheet(id: string): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase.rpc('delete_call_sheet', { p_call_sheet_id: id })
  return fail('delete_call_sheet', error)
}

export async function fetchStaffOptions(): Promise<StaffOption[]> {
  if (!supabase) return []
  const { data, error } = await supabase.rpc('list_call_sheet_crew_options')
  if (error) {
    console.warn(`[supabase] list_call_sheet_crew_options failed: ${error.message}`)
    return []
  }
  return (data as StaffOption[] | null) ?? []
}

/**
 * Replaces the people picked for THIS call sheet. Anyone inherited from an
 * attached project is untouched — they belong to the project's Team, and
 * removing them here would silently disagree with it.
 */
export async function saveCallSheetCrew(
  id: string,
  members: { userId: string; role: string }[],
): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase.rpc('set_call_sheet_crew', {
    p_call_sheet_id: id,
    p_items: members.map((member) => ({ userId: member.userId, role: member.role })),
  })
  return fail('set_call_sheet_crew', error)
}

/* ---------------------------------------------------------------------------
 * Display helpers
 * ------------------------------------------------------------------------- */

/** "Mon 14 Sep 2026", or "No date yet" — never a raw ISO string in front of
    anyone, and never a silently wrong date from timezone-shifted parsing
    (the column is a plain date, so it's split rather than passed to Date). */
export function callSheetDateLabel(iso: string | null): string {
  if (!iso) return 'No date yet'
  const [y, m, d] = iso.split('-').map((part) => Number(part))
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** Today in the viewer's own local calendar, as YYYY-MM-DD — comparable as a
    string against the plain date column, no Date parsing involved. */
export function todayISO(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/** What a call sheet is called when it has no title of its own. */
export function callSheetName(sheet: { title: string | null; projectName?: string | null; clientName: string }): string {
  return sheet.title?.trim() || sheet.projectName?.trim() || sheet.clientName
}

/** The quiet second line on a list row: where this sheet is attached. */
export function callSheetContext(sheet: CallSheetSummary): string {
  const parts = [sheet.clientName, sheet.showName, sheet.projectName].filter(
    (part): part is string => Boolean(part && part.trim()),
  )
  // The client is already the row's own heading group, and a show and project
  // of the same name would read as a stutter.
  const unique = parts.filter((part, index) => parts.indexOf(part) === index)
  return unique.join('  ·  ')
}
